import { c as _c } from "react/compiler-runtime";
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CampaignDeliveryDateRange, CampaignPerformanceSummary, LostReasonCreate, LostReasonSummary, OpportunityAssignmentUpdate, OpportunityBulkArchive, OpportunityBulkCampaignUpdate, OpportunityBulkCreate, OpportunityBulkMarketingConsent, OpportunityBulkStatusUpdate, OpportunityContactLogCreate, OpportunityCreate, OpportunityDetail, OpportunityEnrollmentUpdate, OpportunityFetchParams, OpportunityGroupItem, OpportunityLogDetail, OpportunityNoteCreate, OpportunityStageUpdate, OpportunityStatusUpdate, OpportunitySummary, OpportunityUpdate } from "@/types/opportunity";
import { OpportunityGroupBy } from "@/types/opportunity";
import type { Page } from "@/types/pagination";
import { addOpportunityNote, assignOpportunity, bulkArchiveOpportunities, bulkSetOpportunityCampaign, bulkSetOpportunityMarketingConsent, bulkSetOpportunityStatus, createLostReason, createOpportunitiesBulk, createOpportunity, deleteOpportunity, enrollOpportunityInPipeline, fetchCampaignMetrics, fetchCampaignPerformance, fetchDashboardKPIs, fetchLostReasons, fetchOpportunities, fetchOpportunity, fetchOpportunityActivity, fetchOpportunityGroups, generateOpportunityAISummary, logOpportunityContact, moveOpportunityStage, setOpportunityStatus, syncCampaignDelivery, updateOpportunity, winOpportunity } from "./opportunities";
import { opportunityQueryKeys } from "./opportunity-query-keys";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import { adHierarchyKeys } from "@/query/growth/ad-hierarchy";

// ============================================================================
// Query Keys
// ============================================================================

type OpportunityQueryOptions<TData = unknown> = Omit<UseQueryOptions<TData, Error, TData, readonly unknown[]>, "queryKey" | "queryFn">;
const EMPTY_PAGE: Page<OpportunitySummary> = {
  data: [],
  total: 0
};

// ============================================================================
// Query Options
// ============================================================================

export const opportunityQueryOptions = {
  list: (filters: OpportunityFetchParams) => ({
    queryKey: opportunityQueryKeys.list(filters),
    queryFn: async () => (await fetchOpportunities(filters)) ?? EMPTY_PAGE
  }),
  groupIndex: (groupBy: OpportunityGroupBy, filters: OpportunityFetchParams) => ({
    queryKey: opportunityQueryKeys.groupIndex(groupBy, filters),
    queryFn: (): Promise<OpportunityGroupItem[]> => fetchOpportunityGroups(groupBy, filters),
    // The grouped table reads this from two places — its own empty-state gate
    // and the shared orchestrator — and the second subscriber mounts only once
    // the first has resolved. Without a window they fetch the same counts twice
    // on every visit. Writes invalidate explicitly, so this never hides a
    // change the operator just made.
    staleTime: 30_000
  }),
  detail: (opportunityId: string) => ({
    queryKey: opportunityQueryKeys.detail(opportunityId),
    queryFn: () => fetchOpportunity(opportunityId),
    enabled: !!opportunityId
  }),
  activity: (opportunityId: string) => ({
    queryKey: opportunityQueryKeys.activity(opportunityId),
    queryFn: () => fetchOpportunityActivity(opportunityId),
    enabled: !!opportunityId
  })
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useOpportunities(filters, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== filters) {
    t0 = opportunityQueryOptions.list(filters);
    $[0] = filters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useOpportunity(opportunityId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== opportunityId) {
    t0 = opportunityQueryOptions.detail(opportunityId);
    $[0] = opportunityId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useOpportunityActivity(opportunityId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== opportunityId) {
    t0 = opportunityQueryOptions.activity(opportunityId);
    $[0] = opportunityId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useLostReasons(options) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = opportunityQueryKeys.lostReasons();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0,
      queryFn: fetchLostReasons,
      staleTime: Infinity,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
}

/**
 * Add a loss / disqualification reason without leaving the dialog that needs it.
 * The created reason is folded into the cached list so the dropdown can select
 * it immediately instead of waiting on a refetch.
 */
export function useCreateLostReason() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: created => {
        queryClient.setQueryData(opportunityQueryKeys.lostReasons(), current => {
          if (!current) {
            return [created];
          }
          const withoutCreated = current.filter(reason_0 => reason_0.id !== created.id);
          return [...withoutCreated, created].sort(_temp2);
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// ============================================================================
// Mutations
// ============================================================================
function _temp2(left, right) {
  return left.kind.localeCompare(right.kind) || left.position - right.position || left.label.localeCompare(right.label);
}
function _temp(reason) {
  return createLostReason(reason);
}
export const useOpportunityMutations = () => {
  const $ = _c(111);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: opportunityQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: opportunityQueryKeys.groupIndexes()
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const invalidateOpportunities = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = opportunityId => {
      queryClient.invalidateQueries({
        queryKey: opportunityQueryKeys.detail(opportunityId)
      });
      queryClient.invalidateQueries({
        queryKey: opportunityQueryKeys.activity(opportunityId)
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const invalidateOpportunity = t1;
  let t2;
  if ($[4] !== queryClient) {
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: opportunityQueryKeys.details()
      });
      queryClient.invalidateQueries({
        queryKey: [...opportunityQueryKeys.all, "activity"]
      });
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.all
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const invalidatePersonScopedViews = t2;
  let t3;
  if ($[6] !== queryClient) {
    t3 = async (opportunityId_0, patch) => {
      await queryClient.cancelQueries({
        queryKey: opportunityQueryKeys.lists()
      });
      await queryClient.cancelQueries({
        queryKey: opportunityQueryKeys.detail(opportunityId_0)
      });
      const previousDetail = queryClient.getQueryData(opportunityQueryKeys.detail(opportunityId_0));
      if (previousDetail) {
        queryClient.setQueryData(opportunityQueryKeys.detail(opportunityId_0), {
          ...previousDetail,
          ...patch
        });
      }
      const previousLists = queryClient.getQueriesData({
        queryKey: opportunityQueryKeys.lists()
      });
      previousLists.forEach(t4 => {
        const [queryKey, data] = t4;
        if (data) {
          queryClient.setQueryData(queryKey, {
            ...data,
            data: data.data.map(row => row.id === opportunityId_0 ? {
              ...row,
              ...patch
            } : row)
          });
        }
      });
      return {
        previousLists,
        previousDetail
      };
    };
    $[6] = queryClient;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const optimisticallyPatchOpportunity = t3;
  let t4;
  if ($[8] !== queryClient) {
    t4 = (opportunityId_1, context) => {
      context?.previousLists.forEach(t5 => {
        const [queryKey_0, data_0] = t5;
        queryClient.setQueryData(queryKey_0, data_0);
      });
      if (context?.previousDetail) {
        queryClient.setQueryData(opportunityQueryKeys.detail(opportunityId_1), context.previousDetail);
      }
    };
    $[8] = queryClient;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const rollbackOpportunity = t4;
  let t5;
  if ($[10] !== invalidateOpportunities || $[11] !== invalidatePersonScopedViews) {
    t5 = {
      mutationFn: _temp3,
      onSettled: () => {
        invalidateOpportunities();
        invalidatePersonScopedViews();
      }
    };
    $[10] = invalidateOpportunities;
    $[11] = invalidatePersonScopedViews;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = useMutation(t5);
  let t7;
  if ($[13] !== invalidateOpportunities || $[14] !== invalidatePersonScopedViews) {
    t7 = {
      mutationFn: _temp4,
      onSettled: () => {
        invalidateOpportunities();
        invalidatePersonScopedViews();
      }
    };
    $[13] = invalidateOpportunities;
    $[14] = invalidatePersonScopedViews;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = useMutation(t7);
  let t9;
  if ($[16] !== optimisticallyPatchOpportunity) {
    t9 = t10 => {
      const {
        opportunityId: opportunityId_3,
        opportunity: opportunity_1
      } = t10;
      return optimisticallyPatchOpportunity(opportunityId_3, opportunity_1);
    };
    $[16] = optimisticallyPatchOpportunity;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== rollbackOpportunity) {
    t10 = (_err, variables, context_0) => {
      rollbackOpportunity(variables.opportunityId, context_0);
    };
    $[18] = rollbackOpportunity;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== invalidateOpportunities || $[21] !== invalidatePersonScopedViews) {
    t11 = () => {
      invalidateOpportunities();
      invalidatePersonScopedViews();
    };
    $[20] = invalidateOpportunities;
    $[21] = invalidatePersonScopedViews;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = {
      mutationFn: _temp5,
      onMutate: t9,
      onError: t10,
      onSettled: t11
    };
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = useMutation(t12);
  let t14;
  if ($[27] !== invalidateOpportunities || $[28] !== invalidateOpportunity) {
    t14 = {
      mutationFn: _temp6,
      onSettled: (_data, _err_0, variables_0) => {
        invalidateOpportunities();
        invalidateOpportunity(variables_0.opportunityId);
      }
    };
    $[27] = invalidateOpportunities;
    $[28] = invalidateOpportunity;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = useMutation(t14);
  let t16;
  if ($[30] !== invalidateOpportunities || $[31] !== invalidateOpportunity) {
    t16 = {
      mutationFn: _temp7,
      onSettled: (_data_0, _err_1, variables_1) => {
        invalidateOpportunities();
        invalidateOpportunity(variables_1.opportunityId);
      }
    };
    $[30] = invalidateOpportunities;
    $[31] = invalidateOpportunity;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = useMutation(t16);
  let t18;
  if ($[33] !== optimisticallyPatchOpportunity) {
    t18 = t19 => {
      const {
        opportunityId: opportunityId_7,
        update: update_1
      } = t19;
      return optimisticallyPatchOpportunity(opportunityId_7, {
        currentStageId: update_1.stageId
      });
    };
    $[33] = optimisticallyPatchOpportunity;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== rollbackOpportunity) {
    t19 = (_err_2, variables_2, context_1) => {
      rollbackOpportunity(variables_2.opportunityId, context_1);
    };
    $[35] = rollbackOpportunity;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== invalidateOpportunities || $[38] !== invalidateOpportunity) {
    t20 = (_data_1, _err_3, variables_3) => {
      invalidateOpportunities();
      invalidateOpportunity(variables_3.opportunityId);
    };
    $[37] = invalidateOpportunities;
    $[38] = invalidateOpportunity;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t18 || $[41] !== t19 || $[42] !== t20) {
    t21 = {
      mutationFn: _temp8,
      onMutate: t18,
      onError: t19,
      onSettled: t20
    };
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = useMutation(t21);
  let t23;
  if ($[44] !== optimisticallyPatchOpportunity) {
    t23 = t24 => {
      const {
        opportunityId: opportunityId_9,
        update: update_3
      } = t24;
      return optimisticallyPatchOpportunity(opportunityId_9, {
        status: update_3.status
      });
    };
    $[44] = optimisticallyPatchOpportunity;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== rollbackOpportunity) {
    t24 = (_err_4, variables_4, context_2) => {
      rollbackOpportunity(variables_4.opportunityId, context_2);
    };
    $[46] = rollbackOpportunity;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== invalidateOpportunities || $[49] !== invalidateOpportunity) {
    t25 = (_data_2, _err_5, variables_5) => {
      invalidateOpportunities();
      invalidateOpportunity(variables_5.opportunityId);
    };
    $[48] = invalidateOpportunities;
    $[49] = invalidateOpportunity;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t23 || $[52] !== t24 || $[53] !== t25) {
    t26 = {
      mutationFn: _temp9,
      onMutate: t23,
      onError: t24,
      onSettled: t25
    };
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const t27 = useMutation(t26);
  let t28;
  if ($[55] !== optimisticallyPatchOpportunity) {
    t28 = t29 => {
      const {
        opportunityId: opportunityId_11,
        update: update_5
      } = t29;
      return optimisticallyPatchOpportunity(opportunityId_11, {
        assignedProviderId: update_5.assignedProviderId
      });
    };
    $[55] = optimisticallyPatchOpportunity;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== rollbackOpportunity) {
    t29 = (_err_6, variables_6, context_3) => {
      rollbackOpportunity(variables_6.opportunityId, context_3);
    };
    $[57] = rollbackOpportunity;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== invalidateOpportunities || $[60] !== invalidateOpportunity) {
    t30 = (_data_3, _err_7, variables_7) => {
      invalidateOpportunities();
      invalidateOpportunity(variables_7.opportunityId);
    };
    $[59] = invalidateOpportunities;
    $[60] = invalidateOpportunity;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== t28 || $[63] !== t29 || $[64] !== t30) {
    t31 = {
      mutationFn: _temp10,
      onMutate: t28,
      onError: t29,
      onSettled: t30
    };
    $[62] = t28;
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  const t32 = useMutation(t31);
  let t33;
  let t34;
  if ($[66] !== queryClient) {
    t33 = async opportunityId_13 => {
      await queryClient.cancelQueries({
        queryKey: opportunityQueryKeys.lists()
      });
      const previousLists_0 = queryClient.getQueriesData({
        queryKey: opportunityQueryKeys.lists()
      });
      previousLists_0.forEach(t35 => {
        const [queryKey_1, data_2] = t35;
        if (data_2) {
          queryClient.setQueryData(queryKey_1, {
            ...data_2,
            data: data_2.data.filter(cachedOpportunity => cachedOpportunity.id !== opportunityId_13),
            total: data_2.data.some(cachedOpportunity_0 => cachedOpportunity_0.id === opportunityId_13) ? data_2.total - 1 : data_2.total
          });
        }
      });
      return {
        previousLists: previousLists_0
      };
    };
    t34 = (_err_8, _opportunityId, context_4) => {
      context_4?.previousLists.forEach(t36 => {
        const [queryKey_2, data_3] = t36;
        queryClient.setQueryData(queryKey_2, data_3);
      });
    };
    $[66] = queryClient;
    $[67] = t33;
    $[68] = t34;
  } else {
    t33 = $[67];
    t34 = $[68];
  }
  let t35;
  if ($[69] !== invalidateOpportunities) {
    t35 = () => invalidateOpportunities();
    $[69] = invalidateOpportunities;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t33 || $[72] !== t34 || $[73] !== t35) {
    t36 = {
      mutationFn: _temp11,
      onMutate: t33,
      onError: t34,
      onSettled: t35
    };
    $[71] = t33;
    $[72] = t34;
    $[73] = t35;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  const t37 = useMutation(t36);
  let t38;
  if ($[75] !== invalidateOpportunities || $[76] !== invalidateOpportunity) {
    t38 = {
      mutationFn: _temp12,
      onSettled: (_data_4, _err_9, opportunityId_15) => {
        invalidateOpportunities();
        invalidateOpportunity(opportunityId_15);
      }
    };
    $[75] = invalidateOpportunities;
    $[76] = invalidateOpportunity;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  const t39 = useMutation(t38);
  let t40;
  if ($[78] !== invalidateOpportunity) {
    t40 = {
      mutationFn: _temp13,
      onSettled: (_data_5, _err_10, variables_8) => {
        invalidateOpportunity(variables_8.opportunityId);
      }
    };
    $[78] = invalidateOpportunity;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  const t41 = useMutation(t40);
  let t42;
  if ($[80] !== invalidateOpportunities || $[81] !== invalidateOpportunity) {
    t42 = {
      mutationFn: _temp14,
      onSettled: (_data_6, _err_11, variables_9) => {
        invalidateOpportunities();
        variables_9.opportunityIds.forEach(invalidateOpportunity);
      }
    };
    $[80] = invalidateOpportunities;
    $[81] = invalidateOpportunity;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  const t43 = useMutation(t42);
  let t44;
  if ($[83] !== invalidateOpportunities || $[84] !== invalidateOpportunity) {
    t44 = {
      mutationFn: _temp15,
      onSettled: (_data_7, _err_12, variables_10) => {
        invalidateOpportunities();
        variables_10.opportunityIds.forEach(invalidateOpportunity);
      }
    };
    $[83] = invalidateOpportunities;
    $[84] = invalidateOpportunity;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  const t45 = useMutation(t44);
  let t46;
  if ($[86] !== invalidateOpportunities || $[87] !== invalidatePersonScopedViews) {
    t46 = {
      mutationFn: _temp16,
      onSettled: () => {
        invalidateOpportunities();
        invalidatePersonScopedViews();
      }
    };
    $[86] = invalidateOpportunities;
    $[87] = invalidatePersonScopedViews;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  const t47 = useMutation(t46);
  let t48;
  if ($[89] !== invalidateOpportunities || $[90] !== invalidateOpportunity) {
    t48 = {
      mutationFn: _temp17,
      onSettled: (_data_8, _err_13, variables_11) => {
        invalidateOpportunities();
        variables_11.opportunityIds.forEach(invalidateOpportunity);
      }
    };
    $[89] = invalidateOpportunities;
    $[90] = invalidateOpportunity;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  const t49 = useMutation(t48);
  let t50;
  if ($[92] !== invalidateOpportunity) {
    t50 = {
      mutationFn: _temp18,
      onSettled: (_data_9, _err_14, opportunityId_18) => {
        invalidateOpportunity(opportunityId_18);
      }
    };
    $[92] = invalidateOpportunity;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  const t51 = useMutation(t50);
  let t52;
  if ($[94] !== t13 || $[95] !== t15 || $[96] !== t17 || $[97] !== t22 || $[98] !== t27 || $[99] !== t32 || $[100] !== t37 || $[101] !== t39 || $[102] !== t41 || $[103] !== t43 || $[104] !== t45 || $[105] !== t47 || $[106] !== t49 || $[107] !== t51 || $[108] !== t6 || $[109] !== t8) {
    t52 = {
      create: t6,
      bulkCreate: t8,
      update: t13,
      logContact: t15,
      enroll: t17,
      moveStage: t22,
      setStatus: t27,
      assign: t32,
      delete: t37,
      win: t39,
      addNote: t41,
      bulkSetStatus: t43,
      bulkArchive: t45,
      bulkSetMarketingConsent: t47,
      bulkSetCampaign: t49,
      generateAISummary: t51
    };
    $[94] = t13;
    $[95] = t15;
    $[96] = t17;
    $[97] = t22;
    $[98] = t27;
    $[99] = t32;
    $[100] = t37;
    $[101] = t39;
    $[102] = t41;
    $[103] = t43;
    $[104] = t45;
    $[105] = t47;
    $[106] = t49;
    $[107] = t51;
    $[108] = t6;
    $[109] = t8;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  return t52;
};

// ============================================================================
// Analytics Hooks
// ============================================================================

export function useDashboardKPIs(t0) {
  const $ = _c(7);
  const days = t0 === undefined ? 30 : t0;
  let t1;
  if ($[0] !== days) {
    t1 = opportunityQueryKeys.dashboardKPIs(days);
    $[0] = days;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== days) {
    t2 = () => fetchDashboardKPIs(days);
    $[2] = days;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
}
export function useCampaignMetrics() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: opportunityQueryKeys.campaignMetrics(),
      queryFn: _temp19
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
function _temp19() {
  return fetchCampaignMetrics();
}
export function useCampaignPerformance(campaignId, dateRange) {
  const $ = _c(10);
  let t0;
  if ($[0] !== campaignId || $[1] !== dateRange) {
    t0 = opportunityQueryKeys.campaignPerformance(campaignId, dateRange);
    $[0] = campaignId;
    $[1] = dateRange;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== campaignId || $[4] !== dateRange) {
    t1 = () => fetchCampaignPerformance(campaignId, dateRange);
    $[3] = campaignId;
    $[4] = dateRange;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!campaignId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useSyncCampaignDelivery(campaignId, dateRange) {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== campaignId || $[1] !== dateRange) {
    t0 = () => syncCampaignDelivery(campaignId, dateRange);
    $[0] = campaignId;
    $[1] = dateRange;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== campaignId || $[4] !== dateRange || $[5] !== queryClient) {
    t1 = performance => {
      queryClient.removeQueries({
        queryKey: opportunityQueryKeys.campaignPerformances(campaignId),
        predicate: query => {
          const queryKey = query.queryKey;
          return queryKey[queryKey.length - 2] !== dateRange.startDate || queryKey[queryKey.length - 1] !== dateRange.endDate;
        }
      });
      queryClient.setQueryData(opportunityQueryKeys.campaignPerformance(campaignId, dateRange), performance);
      queryClient.invalidateQueries({
        queryKey: adHierarchyKeys.all
      });
    };
    $[3] = campaignId;
    $[4] = dateRange;
    $[5] = queryClient;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
}
function _temp3(opportunity) {
  return createOpportunity(opportunity);
}
function _temp4(data_1) {
  return createOpportunitiesBulk(data_1);
}
function _temp5(t0) {
  const {
    opportunityId: opportunityId_2,
    opportunity: opportunity_0
  } = t0;
  return updateOpportunity(opportunityId_2, opportunity_0);
}
function _temp6(t0) {
  const {
    opportunityId: opportunityId_4,
    contact
  } = t0;
  return logOpportunityContact(opportunityId_4, contact);
}
function _temp7(t0) {
  const {
    opportunityId: opportunityId_5,
    update
  } = t0;
  return enrollOpportunityInPipeline(opportunityId_5, update);
}
function _temp8(t0) {
  const {
    opportunityId: opportunityId_6,
    update: update_0
  } = t0;
  return moveOpportunityStage(opportunityId_6, update_0);
}
function _temp9(t0) {
  const {
    opportunityId: opportunityId_8,
    update: update_2
  } = t0;
  return setOpportunityStatus(opportunityId_8, update_2);
}
function _temp10(t0) {
  const {
    opportunityId: opportunityId_10,
    update: update_4
  } = t0;
  return assignOpportunity(opportunityId_10, update_4);
}
function _temp11(opportunityId_12) {
  return deleteOpportunity(opportunityId_12);
}
function _temp12(opportunityId_14) {
  return winOpportunity(opportunityId_14);
}
function _temp13(t0) {
  const {
    opportunityId: opportunityId_16,
    note
  } = t0;
  return addOpportunityNote(opportunityId_16, note);
}
function _temp14(update_6) {
  return bulkSetOpportunityStatus(update_6);
}
function _temp15(update_7) {
  return bulkArchiveOpportunities(update_7);
}
function _temp16(update_8) {
  return bulkSetOpportunityMarketingConsent(update_8);
}
function _temp17(update_9) {
  return bulkSetOpportunityCampaign(update_9);
}
function _temp18(opportunityId_17) {
  return generateOpportunityAISummary(opportunityId_17);
}